// "New company" (screen 2): `POST /v1/operator/companies` creates the company, its owner, the
// agency grant and the default metric catalog. The new grant arrives on the next `/v1/me`, so
// both are refetched on success. "Invite an owner now" off: an operator-managed company with no
// owner invitation (internal pilots). The agency manages it and invites plain members from
// People; there is no way to add an owner afterwards yet (only an owner invites an owner).

import { ApiError, newIdempotencyKey } from "@companyos/sdk";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  toast,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";

const CURRENCIES = ["USD", "CAD", "EUR", "GBP", "AUD"] as const;

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);
}

export function NewCompanyDialog({ api, size = "lg" }: { api: Api; size?: "sm" | "lg" }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [currency, setCurrency] = useState<(typeof CURRENCIES)[number]>("USD");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [inviteOwner, setInviteOwner] = useState(true);
  const ids = {
    name: useId(),
    slug: useId(),
    currency: useId(),
    email: useId(),
    owner: useId(),
    invite: useId(),
    hint: useId(),
  };

  const create = useMutation({
    mutationFn: () =>
      api.client.operator.companies.create({
        idempotencyKey: newIdempotencyKey(),
        body: {
          name: name.trim(),
          slug,
          reporting_currency: currency,
          calendar_config: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
            weekStartDay: 1,
            fiscalYearStartMonth: 1,
          },
          ...(inviteOwner
            ? { owner: { email: ownerEmail.trim(), display_name: ownerName.trim() } }
            : {}),
        },
      }),
    onSuccess: async (created) => {
      toast.add({
        title: `${created.company.name} created`,
        description: created.owner_invitation
          ? `Owner invitation sent to ${created.owner_invitation.email}.`
          : "No owner. Your agency manages it; invite members from People. An owner can't be added later yet.",
        type: "success",
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["me"] }),
        queryClient.invalidateQueries({ queryKey: ["operator", "companies"] }),
      ]);
      setOpen(false);
      setName("");
      setSlug("");
      setSlugTouched(false);
      setOwnerEmail("");
      setOwnerName("");
      setInviteOwner(true);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  const error =
    create.error instanceof ApiError
      ? (create.error.problem?.detail ?? create.error.problem?.title)
      : create.error
        ? "The company could not be created."
        : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button size={size} />}>
        <PlusIcon aria-hidden="true" />
        New company
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>New company</DialogTitle>
            <DialogDescription>
              Creates the company, grants your agency operator access and, unless you turn it off,
              invites its owner.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={ids.name}>Company name</FieldLabel>
              <Input
                id={ids.name}
                required
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slugTouched) setSlug(slugify(e.target.value));
                }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.slug}>URL name</FieldLabel>
              <Input
                id={ids.slug}
                required
                pattern="[a-z0-9][a-z0-9-]{1,62}"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value);
                }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.currency}>Reporting currency</FieldLabel>
              <NativeSelect
                id={ids.currency}
                value={currency}
                onChange={(e) => setCurrency(e.target.value as (typeof CURRENCIES)[number])}
              >
                {CURRENCIES.map((c) => (
                  <NativeSelectOption key={c} value={c}>
                    {c}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field orientation="horizontal">
              <Checkbox
                id={ids.invite}
                aria-describedby={inviteOwner ? undefined : ids.hint}
                checked={inviteOwner}
                onCheckedChange={(c) => setInviteOwner(c === true)}
              />
              <FieldLabel htmlFor={ids.invite} className="font-normal">
                Invite an owner now
              </FieldLabel>
            </Field>
            {inviteOwner ? null : (
              <FieldDescription id={ids.hint} className="-mt-2">
                No owner: your agency manages this company and invites members from People. An owner
                can't be added later yet.
              </FieldDescription>
            )}
            {inviteOwner ? (
              <>
                <Field>
                  <FieldLabel htmlFor={ids.owner}>Owner name</FieldLabel>
                  <Input
                    id={ids.owner}
                    required
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor={ids.email}>Owner email</FieldLabel>
                  <Input
                    id={ids.email}
                    type="email"
                    required
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                  />
                </Field>
              </>
            ) : null}
            {error ? <FieldError>{error}</FieldError> : null}
          </FieldGroup>
          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create company"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
